/**
 * rolePermissions — the pure (React-free) frontend permission model.
 *
 * This module mirrors the backend predicates in
 * backend/src/middleware/roleMiddleware.ts. Keep the two in lockstep: the
 * backend contract test (backend/src/__tests__/rbac-contract.test.ts) imports
 * BOTH sides and fails on any drift. See RBAC.md at the repo root.
 */

// RBAC overhaul Phase D (2026-08-06, migration 126): the 'reviewer' role is
// retired — review rights derive from SkillMatrix level >= 4 (Phase C).
export type AppRole = 'superadmin' | 'admin' | 'technician' | 'reader' | 'pending';

export const isAdminLevel = (r: AppRole): boolean => r === 'superadmin' || r === 'admin';

/** Role-derived permission flags (everything except actualRole/isSuperAdmin,
 *  which depend on the real — not impersonated — role). */
export interface RolePermissionFlags {
  /** The effective role (impersonated if active, otherwise actual) */
  role: AppRole;

  // Entity management
  canManageEntities: boolean;
  canCreateWorkOrder: boolean;
  canDeleteWorkOrder: boolean;

  // Test data. NOTE (Phase C/D): there is no role-based "canApproveTest"
  // anymore — Reviewed By is gated per row by SKILL level (>= 4 in the test's
  // skill; see useSignoffTeamLists), and Approved By writes are admin-only.
  canEditTestData: boolean;
  canCheckTest: boolean;
  canChangeTestStatus: boolean;

  // WO grid scheduling
  canEditScheduling: boolean;
  /** WO metadata: status, name, lab location, add-to-schedule (mirrors PUT /workorders/:id) */
  canEditWorkOrderMeta: boolean;

  // Action buttons
  canSaveTestData: boolean;
  canUploadCSV: boolean;
  canAddSample: boolean;
  /** Always true — report generation is open to every authenticated role by
   *  design (readers included). Kept as a named flag so call sites read
   *  intentfully rather than hardcoding `true`. */
  canGenerateReport: boolean;

  // Role checks
  isAdmin: boolean;
  isReader: boolean;
}

export function derivePermissions(role: AppRole): RolePermissionFlags {
  return {
    role,
    canManageEntities: isAdminLevel(role),
    canCreateWorkOrder: isAdminLevel(role),
    canDeleteWorkOrder: isAdminLevel(role),
    canEditTestData: isAdminLevel(role) || role === 'technician',
    canCheckTest: isAdminLevel(role) || role === 'technician',
    canChangeTestStatus: role !== 'reader' && role !== 'pending',
    // RBAC overhaul 2026-08-06 (Phase A): scheduling + WO metadata are
    // admin-only — technicians work tests; their only WO-grid writes are
    // Tested By / Test Date / Test Status (enforced server-side too).
    canEditScheduling: isAdminLevel(role),
    canEditWorkOrderMeta: isAdminLevel(role),
    canSaveTestData: isAdminLevel(role) || role === 'technician',
    canUploadCSV: isAdminLevel(role),
    canAddSample: isAdminLevel(role),
    canGenerateReport: true,
    isAdmin: isAdminLevel(role),
    isReader: role === 'reader',
  };
}
